import React from 'react'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { push } from 'connected-react-router'
import { BForm, BText, BTitle, BLink } from '@prague-digi/ui-components'

import {
  API_ERROR,
  POP_FILES_COUNT,
  POP_UPLOAD_FAILED,
  POP_WRONG_FILE,
} from '../../constants/modals'
import { DragAndDropUpload, FileInfo, PageControlButtons } from '../../components'
import { intl, IntlInterface, wizard, WizardInterface } from '../../hoc'
import { MAX_UPLOAD_FILE_SIZE } from '../../constants/commonConstants'
import { modalDialogActions, modalLoaderActions, popActions, popSelectors } from '../../redux'
import { PopUploadParams } from '../../types/form'
import { ReduxState } from '../../types'
import { Response } from '../../api/api'
import docIcon from '../../assets/documents.svg'
import messages from '../../i18n'
import styles from './styles.scss'

interface Props extends WizardInterface, IntlInterface {
  fetchUploadPopStart: Function
  forceHideModalLoader: Function
  hideModalDialog: Function
  maxDocuments: number
  showModalDialog: Function
  uploadPop: (payload: PopUploadParams) => void
}

interface InnerState {
  files: File[]
}

export class PopUpload extends React.Component<Props> {
  state: InnerState = {
    files: [],
  }

  componentDidMount() {
    if (this.props.fetchUploadPopStart) {
      this.props.fetchUploadPopStart()
    }
  }

  onDrop = (droppedFiles: File[]) => {
    const uniqueFiles: File[] = droppedFiles.reduce(
      (result: File[], file: File) =>
        this.isUniqueFile(file, result) ? [...result, file] : result,
      this.state.files
    )
    const validFiles: File[] = uniqueFiles.filter(
      (file: File) => file.type === 'application/pdf' && file.size <= MAX_UPLOAD_FILE_SIZE
    )
    if (validFiles.length < uniqueFiles.length) {
      this.props.showModalDialog(POP_WRONG_FILE)
    }
    this.setState({
      files: validFiles.slice(0, this.props.maxDocuments),
    })
  }

  onSubmit = () => {
    if (this.state.files.length < this.props.maxDocuments) {
      this.props.showModalDialog(
        POP_FILES_COUNT({
          fileCount: this.state.files.length,
          goNext: () => this.doUpload(this.state.files, false),
        })
      )
    } else {
      this.doUpload(this.state.files, false)
    }
  }

  doUpload = (files: File[], uploadFinished: boolean) => {
    this.props.uploadPop({
      files,
      uploadFinished,
      success: this.props.wizard.gotoNext,
      error: (type: Response) => {
        this.props.forceHideModalLoader()

        this.props.showModalDialog(API_ERROR({ errorInfo: { type } }))
      },
      errorUploadFiles: (filesNotSent: File[], uploadFinished: boolean) => {
        this.props.showModalDialog(POP_UPLOAD_FAILED(this.modalRetry(filesNotSent, uploadFinished)))
      },
    })
  }

  modalRetry = (filesNotSent: File[], uploadFinished: boolean) => () => {
    this.props.hideModalDialog('POP_UPLOAD_FAILED')
    this.doUpload(filesNotSent, uploadFinished)
  }

  filePreviews = () =>
    this.state.files.map((file: File) => (
      <FileInfo
        key={`${file.lastModified}-${file.name}`}
        fileName={file.name}
        remove={() => this.removeFile(file)}
      />
    ))

  removeFile = (removeFile: File) => {
    const filesWithoutRemovedFile = this.state.files.filter(
      file => file.name !== removeFile.name || file.lastModified !== removeFile.lastModified
    )
    this.setState({
      files: filesWithoutRemovedFile,
    })
  }

  isUniqueFile = (newFile: File, files: File[]) =>
    files.reduce(
      (result, file) =>
        result && (file.name !== newFile.name || file.lastModified !== newFile.lastModified),
      true
    )

  uploaderVisible = () => this.state.files.length < this.props.maxDocuments

  filesVisible = () => this.state.files.length > 0

  render() {
    const { translate } = this.props

    return (
      <BForm component="div">
        <BTitle type="h3">{translate(messages.popUpload.title)}</BTitle>
        <BText tooltip={{ id: 'popUploadPerex', text: messages.popUpload.tooltip }}>
          {translate(messages.popUpload.perex)}
        </BText>
        {this.uploaderVisible() && (
          <>
            <DragAndDropUpload onDrop={this.onDrop}>
              <div>
                <img src={docIcon} alt="" className={styles.coverImg} />
                <div>
                  {translate(messages.popUpload.uploadDragOr)}
                  <BLink testid="selectLink" theme={styles.inlineLink}>
                    {translate(messages.popUpload.selectFiles)}
                  </BLink>
                </div>
              </div>
            </DragAndDropUpload>
            <BText type="secondary" theme={styles.uploaderNotice}>
              {translate(messages.popUpload.note)}
            </BText>
          </>
        )}
        {this.filesVisible && <div>{this.filePreviews()}</div>}
        <PageControlButtons
          submitMessage={messages.popUpload.upload}
          onSubmit={this.onSubmit}
          disableSubmit={this.state.files.length === 0}
        />
      </BForm>
    )
  }
}

export default compose(
  connect(
    (state: ReduxState) => ({
      maxDocuments: popSelectors.getMaxDocuments(state),
    }),
    {
      fetchUploadPopStart: popActions.fetchUploadPopStart,
      forceHideModalLoader: modalLoaderActions.forceHideModalLoader,
      hideModalDialog: modalDialogActions.hideModalDialog,
      push,
      showModalDialog: modalDialogActions.showModalDialog,
      uploadPop: popActions.uploadPop,
    }
  ),
  intl,
  wizard
)(PopUpload)
